import format from "format-duration";
import type { IntlShape } from "react-intl";

export function removeUnderscore(str: string) {
  return str.replaceAll("_", " ");
}

export function capitalizeFirstLetter(str: string) {
  return str[0].toUpperCase() + str.slice(1);
}

export function isValidNumericParam(paramId: number | string | undefined | null): boolean {
  if (paramId === null) return false;
  return Number.isInteger(Number(paramId));
}

export function isValidEmail(email: string) {
  return RegExp(/\S+@\S+\.\S+/).test(email);
}

export function approximateDuration(milliseconds: number): number {
  const totalMinutes = Number(milliseconds) / 1000 / 60;
  const rounded = Math.round(totalMinutes / 10) * 10;
  return Math.max(10, rounded);
}

function formatDuration(hours: number, minutes: number, locale: string): string {
  if (typeof Intl !== "undefined" && "DurationFormat" in Intl) {
    return new Intl.DurationFormat(locale, { style: "short" }).format({ hours, minutes });
  }

  const parts: string[] = [];
  if (hours > 0)
    parts.push(new Intl.NumberFormat(locale, { style: "unit", unit: "hour", unitDisplay: "short" }).format(hours));
  if (minutes > 0)
    parts.push(new Intl.NumberFormat(locale, { style: "unit", unit: "minute", unitDisplay: "short" }).format(minutes));
  return parts.join(", ");
}

export const convertToFuzzyTimeFormat = ({
  milliseconds,
  intl,
}: {
  milliseconds: number;
  intl?: IntlShape;
}): string => {
  const totalMinutes = approximateDuration(milliseconds);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  const duration = formatDuration(hours, minutes, intl?.locale ?? "en-US");

  if (intl) {
    return intl.formatMessage(
      { id: "candidate.duration.approximate", defaultMessage: "about {duration}" },
      { duration },
    );
  }
  return `about ${duration}`;
};

export function makeReadableName(name: string) {
  return name.split("_").map(capitalizeFirstLetter).join(" ");
}

export function addSpaces(value: string) {
  return value.split(/(?=[A-Z])/).join(" ");
}

export function secToMin(seconds: number) {
  seconds = Number(seconds);
  return Math.floor(seconds / 60);
}

export function minToSec(minutes: number) {
  minutes = Number(minutes);
  return Math.floor(minutes * 60);
}

export function secToMls(seconds: number) {
  seconds = Number(seconds);
  return seconds * 1000;
}

export function mlsToSec(mls: number) {
  mls = Number(mls);
  return mls / 1000;
}

export function mlsToMin(mls: number) {
  mls = Number(mls);
  return secToMin(mlsToSec(mls));
}

export function secondsToHms(seconds: number) {
  seconds = Number(seconds);
  return format(secToMls(seconds), { leading: true });
}
export function minutesToHms(minutes: number) {
  minutes = Number(minutes);
  return format(secToMls(minToSec(minutes)), { leading: true });
}
